import React, { useState } from 'react';
import styled from 'styled-components';
import { tokens } from "../utils/theme";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const TreeContainer = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? '#0f111a' : '#f5f5f5'};
    padding: 16px;
    border-radius: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.8rem;
    color: ${props => props.theme.palette.mode === 'dark' ? '#a5b3ce' : tokens(props.theme.palette.mode).grey[100]};
    overflow: auto;
    max-height: 400px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
    text-align: left;
    
    /* Custom Scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
    }
`;

const TreeItemBox = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 14px;
    border-left: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    margin-left: 6px;
`;

const TreeRow = styled.div`
    display: flex;
    align-items: center;
    padding: 2px 0;
    cursor: ${props => props.$isExpandable ? 'pointer' : 'default'};
    color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[100] : tokens(props.theme.palette.mode).grey[100]};
    
    &:hover {
        background: ${props => props.$isExpandable ? (props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)') : 'transparent'};
    }
`;

const TreeKey = styled.span`
    font-weight: 600;
    color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).blueAccent[400] : tokens(props.theme.palette.mode).primary[600]};
    margin-right: 8px;
`;

const TreeValue = styled.span`
    color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[300] : tokens(props.theme.palette.mode).grey[200]};
    opacity: 0.9;
`;

const TreeView = ({ data, label, defaultOpen = true, isRoot = false, isSectionRoot = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const isObject = data && typeof data === 'object' && !React.isValidElement(data);
    const isArray = Array.isArray(data);
    const isEmpty = isObject && Object.keys(data).length === 0;

    if (isRoot) {
        if (isObject && !isEmpty) {
            return (
                <div>
                    {Object.entries(data).map(([key, value]) => (
                        <TreeView
                            key={key}
                            label={key}
                            data={value}
                            defaultOpen={true}
                            isSectionRoot={true}
                        />
                    ))}
                </div>
            );
        }
        return null;
    }

    if (!isObject || isEmpty) {
        return (
            <TreeRow $isExpandable={false}>
                {!isSectionRoot && <span style={{ marginRight: '6px', opacity: 0.5 }}>-</span>}
                {label && <TreeKey>{label}:</TreeKey>}
                <TreeValue>{JSON.stringify(data)}</TreeValue>
            </TreeRow>
        );
    }

    const showArrow = !isSectionRoot;

    return (
        <div style={{ marginBottom: '4px' }}>
            <TreeRow
                $isExpandable={showArrow}
                onClick={(e) => {
                    if (!showArrow) return;
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
            >
                {showArrow && (
                    <span style={{ marginRight: '6px', width: '12px', display: 'inline-block' }}>
                        {isOpen ? <FiChevronDown size={14} /> : <FiChevronUp size={14} style={{ transform: 'rotate(90deg)' }} />}
                    </span>
                )}
                {label && <TreeKey style={{ color: isOpen ? undefined : 'inherit', fontWeight: isSectionRoot ? 'bold' : 'normal' }}>{label}</TreeKey>}
                {!label && Array.isArray(data) && <span style={{ opacity: 0.5 }}>[{data.length}]</span>}
            </TreeRow>

            {isOpen && (
                <TreeItemBox style={{ paddingLeft: isSectionRoot ? '0px' : '14px', borderLeft: isSectionRoot ? 'none' : undefined, marginLeft: isSectionRoot ? '0px' : '6px' }}>
                    {isArray ? (
                        data.map((item, index) => (
                            <TreeView key={index} data={item} />
                        ))
                    ) : (
                        Object.entries(data).map(([key, value]) => (
                            <TreeView key={key} label={key} data={value} defaultOpen={true} />
                        ))
                    )}
                </TreeItemBox>
            )}
        </div>
    );
};

export { TreeView, TreeContainer, TreeItemBox, TreeRow, TreeKey, TreeValue };
